import _ from 'underscore';
import { DismissMode, INotification, NotificationLevel, ScopeLevel, TargetComponent, IContext } from '../../types';
import { i18nMap, isAvailable, isBillingLinkPermission } from '../../utils';
import { getAlertFlags, IAlertFlags } from './alert-information';

/**
 * ManualTest:
 * 1. Prepare fiddler mock data/real failed import
 * 2. Lauch site with fiddler opened
 * 3. Check the notificaiton in UNQ
 * 4. Go to disapproved keywords or disapproved ads page
 */
export class DisapprovedEntities {
  context: IContext;
  i18n: any;
  availableScopes?: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = this.context.getNewI18n();
    this.availableScopes = [ScopeLevel.account];
  }

  async getDisapprovedEntityNotifications(): Promise<INotification[]> {
    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    const scope = this.context.getCurrentScope();
    if (!isAvailable(scope, this.availableScopes)) {
      return [];
    }

    const permission = this.context.getCurrentPermissions();

    const { IsGoogleImportVideoAdsEnabled, IsGoogleImportVideoAdsImprovementEnabled } = permission;

    const { SmartNewNotification } = permission.dynamic;
    const select = ['RejectedKeywordsCountV2', 'RejectedAdsCountV2'];
    if (!scope.values.adGroupId) {
      select.push('RejectedSmartCampaignAdsCountV2');
    }
    if (scope.values.aid && !scope.values.campaignId) {
      select.push('RejectedSmartListingsCount');
    }

    if (IsGoogleImportVideoAdsEnabled && IsGoogleImportVideoAdsImprovementEnabled) {
      select.push('RejectedAdResourceCount');
    }

    const alertFlags: IAlertFlags = await getAlertFlags(select, scope);

    const hasDisapprovedAds = this.getNoneSmartDisapprovedAdsCount(alertFlags) > 0;
    const hasDisapprovedKeywords = this.getRejectedKeywordsCount(alertFlags) > 0;
    const hasDisapprovedSmartAds = this.getRejectedSmartCampaignAdsCount(alertFlags) > 0;
    const hasDisapprovedSmartListing = alertFlags.RejectedSmartListingsCount > 0;
    const hasDisapprovedAdResource = alertFlags.RejectedAdResourceCount > 0;

    const notifications = [];
    if (hasDisapprovedAds && hasDisapprovedKeywords) {
      notifications.push(this.getDisapprovedBothKeywordsAndAdsNotification(alertFlags));
    } else if (hasDisapprovedAds) {
      notifications.push(this.getDisapprovedAdsNotification(alertFlags));
    } else if (hasDisapprovedKeywords) {
      notifications.push(this.getDisapprovedKeywordsNotification(alertFlags));
    }

    if (SmartNewNotification) {
      if (hasDisapprovedSmartAds) {
        notifications.push(this.getDisapprovedSmartCampaignAdsNotification(alertFlags));
      }

      if (hasDisapprovedSmartListing) {
        notifications.push(this.getDisapprovedSmartListingNotification(alertFlags));
      }
    }

    if (hasDisapprovedAdResource) {
      notifications.push(this.getDisapprovedAdResourceNotification(alertFlags));
    }

    return _.compact(notifications);
  }

  navigateToDisapprovedKeywords() {
    this.context.route.goDisapprovedKeywords();
  }

  navigateToDisapprovedAds() {
    this.context.route.goDisapprovedAds();
  }

  navigateToDisapprovedAdResource() {
    this.context.route.goDisapprovedAdResource();
  }

  navigateToAllSmartCampaigns() {
    this.context.route.goAllSmartCampaigns();
  }

  getRejectedAdsCount(alertFlags: IAlertFlags): number {
    return alertFlags.RejectedAdsCountV2;
  }

  getRejectedKeywordsCount(alertFlags: IAlertFlags): number {
    return alertFlags.RejectedKeywordsCountV2;
  }

  getRejectedSmartCampaignAdsCount(alertFlags: IAlertFlags): number {
    return alertFlags.RejectedSmartCampaignAdsCountV2;
  }

  getNoneSmartDisapprovedAdsCount(alertFlags: IAlertFlags): number {
    return this.getRejectedAdsCount(alertFlags) - this.getRejectedSmartCampaignAdsCount(alertFlags);
  }

  getDisapprovedBothKeywordsAndAdsNotification(alertFlags: IAlertFlags): INotification {
    const numberOfDisapprovedAds = this.i18n.formatDecimalToInteger(this.getNoneSmartDisapprovedAdsCount(alertFlags));
    const numberOfDisapprovedKeywords = this.i18n.formatDecimalToInteger(this.getRejectedKeywordsCount(alertFlags));

    return {
      id: 'Notification_DisapprovedEntity',
      note: 'KeywordsAndAds',
      condition: true,
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.adGroup,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_DisapprovedEntityMessageTemplate_New_Title),
      message: this.i18n.getString(
        i18nMap.UnifiedNotificationQueue_Alert_TwoDisapprovedEntityMessageTemplate_New_Message,
        {
          disapprovedAds: this.i18n.getString(i18nMap.Alert_DisapprovedAds),
          numberOfDisapprovedAds,
          disapprovedKeywords: this.i18n.getString(i18nMap.Alert_DisapprovedKeywords),
          numberOfDisapprovedKeywords,
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewAds),
          action: () => {
            this.navigateToDisapprovedAds();
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewKeywords),
          action: () => {
            this.navigateToDisapprovedKeywords();
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getDisapprovedKeywordsNotification(alertFlags: IAlertFlags): INotification {
    const numberOfDisapprovedKeywords = this.i18n.formatDecimalToInteger(this.getRejectedKeywordsCount(alertFlags));
    return {
      id: 'Notification_DisapprovedEntity',
      condition: true,
      note: 'Keywords',
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.adGroup,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_DisapprovedEntityMessageTemplate_New_Title),
      message: this.i18n.getString(
        i18nMap.UnifiedNotificationQueue_Alert_OneDisapprovedEntityMessageTemplate_New_Message,
        {
          disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedKeywords),
          numberOfDisapprovedEntities: numberOfDisapprovedKeywords,
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewAdsOrKeywords, {
            disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedKeywords),
            numberOfDisapprovedEntities: numberOfDisapprovedKeywords,
          }),
          action: () => {
            this.navigateToDisapprovedKeywords();
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getDisapprovedAdsNotification(alertFlags: IAlertFlags): INotification {
    const numberOfDisapprovedAds = this.i18n.formatDecimalToInteger(this.getNoneSmartDisapprovedAdsCount(alertFlags));
    return {
      id: 'Notification_DisapprovedEntity',
      condition: true,
      note: 'Ads',
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.adGroup,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_DisapprovedEntityMessageTemplate_New_Title),
      message: this.i18n.getString(
        i18nMap.UnifiedNotificationQueue_Alert_OneDisapprovedEntityMessageTemplate_New_Message,
        {
          disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedAds),
          numberOfDisapprovedEntities: numberOfDisapprovedAds,
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewAdsOrKeywords, {
            disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedAds),
            numberOfDisapprovedEntities: numberOfDisapprovedAds,
          }),
          action: () => {
            this.navigateToDisapprovedAds();
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getDisapprovedAdResourceNotification(alertFlags: IAlertFlags): INotification {
    const numberOfDisapprovedAds = alertFlags.RejectedAdResourceCount;
    return {
      id: 'Notification_DisapprovedAdResource',
      condition: true,
      note: 'Ads',
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.adGroup,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_DisapprovedAdResource_New_Title),
      message: this.i18n.getString(
        i18nMap.UnifiedNotificationQueue_Alert_OneDisapprovedAdresource_New_Message,
        {
          disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedAds),
          numberOfDisapprovedEntities: numberOfDisapprovedAds,
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewAdsOrKeywords, {
            disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedAds),
            numberOfDisapprovedEntities: numberOfDisapprovedAds,
          }),
          action: () => {
            this.navigateToDisapprovedAdResource();
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getDisapprovedSmartCampaignAdsNotification(alertFlags: IAlertFlags): INotification {
    const numberOfDisapprovedAds = this.i18n.formatDecimalToInteger(this.getRejectedSmartCampaignAdsCount(alertFlags));
    return {
      id: 'Notification_DisapprovedSmartAds',
      condition: true,
      note: 'Ads',
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.campaign,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_DisapprovedSmartCampaignAd_Title),
      message: this.i18n.getString(
        i18nMap.UnifiedNotificationQueue_Alert_DisapprovedSmartCampaignAd_Message,
        {
          number: numberOfDisapprovedAds,
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewAdsOrKeywords, {
            disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedAds),
            numberOfDisapprovedEntities: numberOfDisapprovedAds,
          }),
          action: () => {
            this.navigateToAllSmartCampaigns();
          },
          callbackType: 'closeQueue',
          extraClassNames: ['smart-disapproved-ads-notification'],
          extraAttrs: undefined,
        },
      ],
    };
  }

  getDisapprovedSmartListingNotification(alertFlags: IAlertFlags): INotification {
    const number = this.i18n.formatDecimalToInteger(alertFlags.RejectedSmartListingsCount);
    return {
      id: 'Notification_DisapprovedSmartListings',
      condition: true,
      note: 'Ads',
      severity: NotificationLevel.Warning,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_DisapprovedSmartListingV2_Title),
      message: this.i18n.getString(
        i18nMap.UnifiedNotificationQueue_Alert_DisapprovedSmartListingV2_Message,
        { number }
      ),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ReviewAdsOrKeywords, {
            disapprovedEntity: this.i18n.getString(i18nMap.Alert_DisapprovedSmartListing_V2),
            numberOfDisapprovedEntities: number,
          }),
          action: () => {
            this.navigateToAllSmartCampaigns();
          },
          callbackType: 'closeQueue',
          extraClassNames: ['smart-disapproved-listing-notification'],
          extraAttrs: undefined,
        },
      ],
    };
  }
}
